import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Trip, TripListResponse, CreateTripDto, TripStatus, BillingStatus, TripAvailability, TripPhoto, LivePositionsResponse, TripGpsHistoryResponse, TripHistoryResponse, TripStop, TripStopInput, ConfirmationPreview, ConfirmationPreviewRow } from '../models/trip.model';

/** What GET /trips/counts returns — the sidebar badge numbers. */
export interface TripCounts { total: number; active: number; billingOpen: number; toConfirm: number; }

@Injectable({ providedIn: 'root' })
export class TripService {
  private readonly url = `${environment.apiUrl}/trips`;
  constructor(private http: HttpClient) {}

  private nocache() {
    return new HttpHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' });
  }

  list(params?: { status?: TripStatus; from?: string; to?: string; page?: number; limit?: number }): Observable<TripListResponse> {
    let p = new HttpParams().set('_t', Date.now().toString());
    if (params?.status) p = p.set('status', params.status);
    if (params?.from) p = p.set('from', params.from);
    if (params?.to) p = p.set('to', params.to);
    if (params?.page) p = p.set('page', String(params.page));
    if (params?.limit) p = p.set('limit', String(params.limit));
    return this.http.get<TripListResponse>(this.url, { params: p, headers: this.nocache() });
  }

  /** Sidebar badge numbers — four counts instead of a 500-trip payload.
   *  `toConfirm` is the "Zur Bestätigung" badge; the server counts it by the
   *  same rule as awaitingConfirmation() in trip.model.ts (WP1455). */
  counts(): Observable<TripCounts> {
    return this.http.get<TripCounts>(
      `${this.url}/counts`, { headers: this.nocache(), params: { _t: Date.now().toString() } });
  }

  get(id: string): Observable<Trip> {
    return this.http.get<Trip>(`${this.url}/${id}`, { headers: this.nocache(), params: { _t: Date.now().toString() } });
  }

  create(dto: CreateTripDto): Observable<Trip> { return this.http.post<Trip>(this.url, dto); }
  update(id: string, dto: Partial<CreateTripDto>): Observable<Trip> { return this.http.put<Trip>(`${this.url}/${id}`, dto); }
  /** Delete a booking entirely (also unlinks any DMS document it was created from). */
  deleteTrip(id: string): Observable<{ ok: boolean }> { return this.http.delete<{ ok: boolean }>(`${this.url}/${id}`); }
  updateStatus(id: string, status: TripStatus): Observable<Trip> { return this.http.patch<Trip>(`${this.url}/${id}/status`, { status }); }
  /** Assign either or both. Pass null to leave a side empty — an empty string
   *  would fail the endpoint's isUUID() check, which only skips null/undefined. */
  assign(id: string, driverId?: string | null, vehicleId?: string | null, helperDriverIds?: string[]): Observable<Trip> {
    return this.http.patch<Trip>(`${this.url}/${id}/assign`, {
      driverId: driverId || null, vehicleId: vehicleId || null, helperDriverIds,
    });
  }
  cancel(id: string, reason?: string): Observable<Trip> { return this.http.patch<Trip>(`${this.url}/${id}/cancel`, { reason }); }

  availability(at: string, excludeTripId?: string): Observable<TripAvailability> {
    let p = new HttpParams().set('at', at).set('_t', Date.now().toString());
    if (excludeTripId) p = p.set('excludeTripId', excludeTripId);
    return this.http.get<TripAvailability>(`${this.url}/availability`, { params: p, headers: this.nocache() });
  }

  /** Latest GPS fix per active trip — drives the dashboard live-position marker. */
  livePositions(): Observable<LivePositionsResponse> {
    return this.http.get<LivePositionsResponse>(`${this.url}/live-positions`, {
      params: { _t: Date.now().toString() }, headers: this.nocache(),
    });
  }

  /** Fill in whatever route figures are missing. `force` recomputes figures
   *  that are present but stale — what Settings sends after the HQ moves. */
  computeRoute(id: string, force = false): Observable<Trip> {
    return this.http.patch<Trip>(`${this.url}/${id}/route`, force ? { force: true } : {});
  }

  /** Recorded GPS trail of a trip (location history), oldest fix first. */
  gpsHistory(id: string): Observable<TripGpsHistoryResponse> {
    return this.http.get<TripGpsHistoryResponse>(`${this.url}/${id}/gps`, {
      headers: this.nocache(), params: { _t: Date.now().toString() },
    });
  }
  sendConfirmation(id: string): Observable<Trip> { return this.http.patch<Trip>(`${this.url}/${id}/confirmation`, {}); }
  sendConfirmationsBulk(ids: string[]): Observable<{ count: number }> { return this.http.post<{ count: number }>(`${this.url}/confirmations`, { ids }); }

  // ── Confirmation preview (nothing is sent by any of these) ──
  /** The mail exactly as it would go out: subject, resolved recipients and the
   *  real rendered body. What the dispatcher reads before pressing send. */
  confirmationPreview(id: string): Observable<ConfirmationPreview> {
    return this.http.get<ConfirmationPreview>(`${this.url}/${id}/confirmation/preview`, {
      headers: this.nocache(), params: { _t: Date.now().toString() },
    });
  }
  /** Which bookings a bulk send would mail, and to which addresses. */
  confirmationPreviewBulk(ids: string[]): Observable<{ bookings: ConfirmationPreviewRow[] }> {
    return this.http.post<{ bookings: ConfirmationPreviewRow[] }>(`${this.url}/confirmations/preview`, { ids });
  }

  // ── Tour stops ──
  stops(id: string): Observable<{ stops: TripStop[] }> {
    return this.http.get<{ stops: TripStop[] }>(`${this.url}/${id}/stops`, {
      headers: this.nocache(), params: { _t: Date.now().toString() },
    });
  }
  /** Replace the whole ordered tour — add, remove and reorder are all this one
   *  call, so a half-written tour can never be saved. An empty array turns a
   *  tour back into an ordinary booking. */
  saveStops(id: string, stops: TripStopInput[]): Observable<{ stops: TripStop[]; trip: Trip }> {
    return this.http.put<{ stops: TripStop[]; trip: Trip }>(`${this.url}/${id}/stops`, { stops });
  }
  setBillingStatus(id: string, billingStatus: BillingStatus): Observable<Trip> { return this.http.patch<Trip>(`${this.url}/${id}/billing`, { billingStatus }); }
  submitBillingBulk(ids: string[]): Observable<{ count: number }> { return this.http.post<{ count: number }>(`${this.url}/billing/submit`, { ids }); }

  // ── Attachments (trip_photos) ──
  listAttachments(id: string): Observable<TripPhoto[]> {
    return this.http.get<TripPhoto[]>(`${this.url}/${id}/photos`, { headers: this.nocache(), params: { _t: Date.now().toString() } });
  }
  /** Upload one or more files in a single request (field name `files`). */
  uploadAttachments(id: string, files: File[]): Observable<TripPhoto[]> {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return this.http.post<TripPhoto[]>(`${this.url}/${id}/photos`, fd);
  }
  deleteAttachment(id: string, photoId: string): Observable<{ ok: boolean }> {
    return this.http.delete<{ ok: boolean }>(`${this.url}/${id}/photos/${photoId}`);
  }
  /** Fetch an attachment as a blob (auth header attached by the interceptor),
      so the UI can save it under its original filename. */
  downloadAttachment(id: string, photoId: string): Observable<Blob> {
    return this.http.get(`${this.url}/${id}/photos/${photoId}/download`, { responseType: 'blob' });
  }

  /** All attachments of a booking as one zip — one save prompt instead of one per file. */
  downloadAllAttachments(id: string): Observable<Blob> {
    return this.http.get(`${this.url}/${id}/photos/download-all`, { responseType: 'blob' });
  }

  // ── Booking history (audit trail) ──
  /** Everything that happened to a booking and every edit, newest first. */
  history(id: string): Observable<TripHistoryResponse> {
    return this.http.get<TripHistoryResponse>(`${this.url}/${id}/history`, {
      headers: this.nocache(), params: { _t: Date.now().toString() },
    });
  }
  /** Mark the booking as opened by the current user — what clears "Ungelesen".
   *  Idempotent: the server records the first view per user and ignores the rest. */
  markSeen(id: string): Observable<{ ok: boolean; firstView: boolean }> {
    return this.http.post<{ ok: boolean; firstView: boolean }>(`${this.url}/${id}/seen`, {});
  }

  /** Download a booking as a PDF (server-rendered, same layout as the email). */
  pdf(id: string): Observable<Blob> {
    return this.http.get(`${this.url}/${id}/pdf`, { responseType: 'blob' });
  }
}
